import { Suspense, lazy, useLayoutEffect, useRef, type ReactNode } from 'react';
import { currentUiVersion, applyUiVersion } from './lib/uiVersion';

/* ============================================================
   UI VERSION ROUTING

   `UiRoute` is the single seam between the two trees. App.tsx
   wraps a route's element in it and passes both versions:

     <UiRoute v1={<Login />} v2={<V2Login />} />

   The v2 element is a `lazy()` component, so its chunk — screens,
   tokens, base CSS, fonts — is only fetched when a v2 session
   actually renders it. A v1 member downloads none of it.

   The version is read from the document, which the boot script in
   index.html stamped synchronously before React mounted. Reading
   it per render rather than holding it in state is deliberate:
   there is no subscription and no way for a route to flip
   mid-session, which is exactly the guarantee we want (see the
   never-flip-mid-session note in lib/uiVersion.ts).
   ============================================================ */

/** Screens ported to v2. Add one line per screen as it lands. */
export const V2Welcome = lazy(() =>
  import('./entry').then((m) => ({ default: m.Welcome })),
);

export const V2Login = lazy(() =>
  import('./entry').then((m) => ({ default: m.Login })),
);

export const V2Dashboard = lazy(() =>
  import('./entry').then((m) => ({ default: m.Dashboard })),
);

export const V2BookingType = lazy(() =>
  import('./entry').then((m) => ({ default: m.BookingType })),
);

export const V2SelectSlots = lazy(() =>
  import('./entry').then((m) => ({ default: m.SelectSlots })),
);

export const V2AllBookings = lazy(() =>
  import('./entry').then((m) => ({ default: m.AllBookings })),
);

export const V2BookingSuccess = lazy(() =>
  import('./entry').then((m) => ({ default: m.BookingSuccess })),
);

export const V2SessionDetails = lazy(() =>
  import('./entry').then((m) => ({ default: m.SessionDetails })),
);

export const V2MyGame = lazy(() =>
  import('./entry').then((m) => ({ default: m.MyGame })),
);

/**
 * The in-app full assessment report and the coaching-notes history.
 * Both v2-only — there is no v1 screen behind either — so they render
 * directly through `UiV2Route` rather than `UiRoute`, the same pattern
 * `V2MyPlan` above uses for `/profile/my-plan`.
 */
export const V2MyGameReport = lazy(() =>
  import('./entry').then((m) => ({ default: m.MyGameReport })),
);

export const V2MyGameNotesHistory = lazy(() =>
  import('./entry').then((m) => ({ default: m.MyGameNotesHistory })),
);
export const V2NoteThreadScreen = lazy(() =>
  import('./entry').then((m) => ({ default: m.NoteThreadScreen })),
);
export const V2TopicsScreen = lazy(() =>
  import('./entry').then((m) => ({ default: m.TopicsScreen })),
);
export const V2NewTopicScreen = lazy(() =>
  import('./entry').then((m) => ({ default: m.NewTopicScreen })),
);
export const V2ThreadScreen = lazy(() =>
  import('./entry').then((m) => ({ default: m.ThreadScreen })),
);

export const V2MyGamePlan = lazy(() =>
  import('./entry').then((m) => ({ default: m.MyGamePlan })),
);

export const V2Shop = lazy(() =>
  import('./entry').then((m) => ({ default: m.Shop })),
);

export const V2ProductDetail = lazy(() =>
  import('./entry').then((m) => ({ default: m.ProductDetail })),
);

export const V2Cart = lazy(() =>
  import('./entry').then((m) => ({ default: m.Cart })),
);

export const V2OrderSuccess = lazy(() =>
  import('./entry').then((m) => ({ default: m.OrderSuccess })),
);

export const V2MyOrders = lazy(() =>
  import('./entry').then((m) => ({ default: m.MyOrders })),
);

export const V2OrderDetails = lazy(() =>
  import('./entry').then((m) => ({ default: m.OrderDetails })),
);

export const V2Profile = lazy(() =>
  import('./entry').then((m) => ({ default: m.Profile })),
);

/* Account is v2-only. It absorbs the standalone My Orders and
   Notification Preferences rows, which still have their own v1
   screens and routes — those stay reachable, Profile just no
   longer points at them directly in v2. */
export const V2Account = lazy(() =>
  import('./entry').then((m) => ({ default: m.Account })),
);

export const V2Membership = lazy(() =>
  import('./entry').then((m) => ({ default: m.Membership })),
);

/* My Plan is v2-only — there is no v1 screen behind it, so
   `/profile/my-plan` renders it directly rather than through
   `UiRoute`. The old `/profile/membership` route stays as it was
   for the v1 tree and for anything still linking to it. */
export const V2MyPlan = lazy(() =>
  import('./entry').then((m) => ({ default: m.MyPlan })),
);

export const V2PersonalInformation = lazy(() =>
  import('./entry').then((m) => ({ default: m.PersonalInformation })),
);

export const V2UpdatePaymentMethod = lazy(() =>
  import('./entry').then((m) => ({ default: m.UpdatePaymentMethod })),
);

/* Community is v2-only: there is no v1 screen behind it, so these
   render through `UiV2Route` directly. A v1 session never sees the
   tab, because `TabBar` is a v2 component. */
export const V2Community = lazy(() =>
  import('./entry').then((m) => ({ default: m.Community })),
);

export const V2CommunityMember = lazy(() =>
  import('./entry').then((m) => ({ default: m.CommunityMember })),
);

export const V2CommunitySettings = lazy(() =>
  import('./entry').then((m) => ({ default: m.CommunitySettings })),
);

/** "Your Tee Buddies" — requests, connections and mutes. */
export const V2TeeBuddies = lazy(() =>
  import('./entry').then((m) => ({ default: m.TeeBuddies })),
);

/** The in-app scanner, behind Community's QR header icon. */
export const V2CommunityScan = lazy(() =>
  import('./entry').then((m) => ({ default: m.CommunityScan })),
);

/**
 * The notification inbox, behind Community's bell.
 *
 * v2-only, and not under `/community` in the URL: the list carries every
 * push the member has been sent — bookings, orders, membership — not only
 * Community's, so nesting it there would misname it.
 */
export const V2Notifications = lazy(() =>
  import('./entry').then((m) => ({ default: m.Notifications })),
);

/** Where a scanned QR and a shared connect link land. */
export const V2CommunityConnect = lazy(() =>
  import('./entry').then((m) => ({ default: m.CommunityConnect })),
);

/** "Share a tee time" — the structured post, and the round behind it. */
export const V2ShareTeeTime = lazy(() =>
  import('./entry').then((m) => ({ default: m.ShareTeeTime })),
);

export const V2TeeTimeDetail = lazy(() =>
  import('./entry').then((m) => ({ default: m.TeeTimeDetail })),
);

/**
 * The public, OTP-gated share-report microsite (`/report/:shareId`).
 * Always v2 — see `PublicV2Route` below — so it has no v1
 * counterpart and gets its own lazy export rather than a `UiRoute`
 * pairing.
 */
export const V2ShareReport = lazy(() =>
  import('./entry').then((m) => ({ default: m.ShareReport })),
);

/**
 * The past-due takeover. Not a screen behind a route — v1's
 * `HomeLayout` and `MemberAccessGuard` swap it in for whatever the
 * member asked for — so it gets `UiBlock` below rather than
 * `UiRoute`.
 */
export const V2PastDueGate = lazy(() =>
  import('./entry').then((m) => ({ default: m.V2PastDueGate })),
);

export const V2WaiverSigning = lazy(() =>
  import('./entry').then((m) => ({ default: m.WaiverSigning })),
);

/** Also a block, not a route — see `V2PastDueGate` above. */
export const V2MembershipStatusBanner = lazy(() =>
  import('./entry').then((m) => ({ default: m.V2MembershipStatusBanner })),
);

/** The desktop installer card. Block, not a route — `App` renders
 *  it next to the router so it survives navigation. */
export const V2InstallPrompt = lazy(() =>
  import('./entry').then((m) => ({ default: m.V2InstallPrompt })),
);

/**
 * Renders the v2 element for a v2 session, the v1 element
 * otherwise. Routes with no v2 counterpart yet simply aren't
 * wrapped — they keep serving v1 to everyone.
 */
export function UiRoute({ v1, v2 }: { v1: ReactNode; v2: ReactNode }) {
  if (currentUiVersion() !== 'v2') return <>{v1}</>;

  return <UiV2Route>{v2}</UiV2Route>;
}

/**
 * The Suspense boundary a lazy v2 route element needs, without a
 * v1 counterpart to fall back to. Used by v2-only routes such as
 * `/profile/my-plan`.
 *
 * A lazy element rendered with no boundary above it suspends the
 * whole tree on a click-driven navigation, which React reports as
 * error #426 ("a component suspended while responding to
 * synchronous input") and which blanks the app — so every lazy v2
 * route element must go through this or `UiRoute`.
 */
export function UiV2Route({ children }: { children: ReactNode }) {
  return (
    /* The fallback is a bare branded surface, not a spinner. This
       resolves in a few hundred ms on a warm cache, and a spinner
       that flashes for 200ms reads as jank. A stable background
       does not. */
    <Suspense
      fallback={
        <div
          style={{
            minHeight: '100dvh',
            background: 'var(--tb-bg, #FFFFFF)',
          }}
        />
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * Same seam as `UiRoute`, for a block a v1 component renders
 * inline rather than as a route element — the past-due gate and
 * the membership status strip. Neither has a URL, and reading
 * `UiRoute` at those call sites would suggest otherwise.
 *
 * The difference that matters is the fallback. `UiRoute` holds a
 * full-viewport surface while the chunk loads, which is right for
 * a page and wrong for a strip pinned to the top of one — that
 * would paint a blank screen over the content it is annotating.
 * Blocks render nothing until they resolve, and callers that are
 * page-sized pass their own placeholder.
 *
 * In practice the fallback is rarely seen: a v2 block only renders
 * inside a v2 session, which means some v2 route already pulled
 * `entry` in.
 */
export function UiBlock({
  v1,
  v2,
  fallback = null,
}: {
  v1: ReactNode;
  v2: ReactNode;
  fallback?: ReactNode;
}) {
  if (currentUiVersion() !== 'v2') return <>{v1}</>;
  return <Suspense fallback={fallback}>{v2}</Suspense>;
}

/**
 * Forces v2 for a surface that has no session to resolve a version
 * from — currently just the public, unauthenticated share-report
 * microsite (`/report/:shareId`). `UiRoute` above reads whatever
 * `data-ui` the boot script already stamped from the member's
 * cached/server-resolved version; an anonymous visitor has neither,
 * so this stamps it directly.
 *
 * `useLayoutEffect`, not `useEffect` — it must land before the
 * browser paints, or the OTP screen flashes unstyled for one frame
 * (every `--tb-*` token and `.tb-*` class is scoped to
 * `html[data-ui="v2"]`; see tokens.css's SCOPE comment).
 *
 * Calls `applyUiVersion` ONLY, never `setUiVersion` — nothing here
 * may write to `localStorage`. This is an anonymous third party, not
 * a member; persisting a version choice for them would silently flip
 * a real member's own device the next time they open the app on the
 * same browser (T-369-35 in the 369-04 threat model). The previous
 * `data-ui` value is restored on unmount so navigating away leaves
 * no trace.
 */
export function PublicV2Route({ children }: { children: ReactNode }) {
  const previousUiRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    previousUiRef.current = document.documentElement.getAttribute('data-ui');
    applyUiVersion('v2');
    return () => {
      const previous = previousUiRef.current;
      if (previous) {
        document.documentElement.setAttribute('data-ui', previous);
      } else {
        document.documentElement.removeAttribute('data-ui');
      }
    };
  }, []);

  return (
    <Suspense
      fallback={
        <div
          style={{
            minHeight: '100dvh',
            background: 'var(--tb-bg, #FFFFFF)',
          }}
        />
      }
    >
      {children}
    </Suspense>
  );
}
